import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Eye, TrendingUp } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const api = {
  runs: () => apiClient.get('/payroll/runs').then((r) => r.data),
  payslips: (id) => apiClient.get(`/payroll/runs/${id}/payslips`).then((r) => r.data),
  markPosted: (id) => apiClient.post(`/payroll/runs/${id}/mark-posted`).then((r) => r.data),
};

const AccountsQueuePage = () => {
  const [runs, setRuns] = useState([]);
  const [showDetail, setShowDetail] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.runs();
      setRuns((r.data || []).filter((x) => x.handed_off));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const openDetail = async (run) => {
    setShowDetail(run);
    try { const r = await api.payslips(run.id); setPayslips(r.data || []); }
    catch { setPayslips([]); }
  };
  const markPosted = async (run) => {
    if (!window.confirm(`Mark ${run.label || 'this run'} as posted to Books? This locks it permanently.`)) return;
    try {
      await api.markPosted(run.id);
      toast({ title: 'Marked posted to Books' });
      setShowDetail(null);
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const pending = runs.filter((r) => r.accounts_status !== 'posted');
  const posted = runs.filter((r) => r.accounts_status === 'posted');
  const totalPending = pending.reduce((s, r) => s + (r.stats?.net || 0), 0);
  const totalPosted = posted.reduce((s, r) => s + (r.stats?.net || 0), 0);
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Accounts Inbox</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Payroll queue</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Every payroll run HR has handed off. One click to post to Books ledger.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Tile label="Pending post" v={pending.length} accent="amber" />
        <Tile label="Net awaiting post" v={totalPending.toLocaleString(undefined, { maximumFractionDigits: 0 })} accent="amber" />
        <Tile label="Posted (all-time)" v={posted.length} accent="emerald" />
        <Tile label="Net posted" v={totalPosted.toLocaleString(undefined, { maximumFractionDigits: 0 })} accent="emerald" />
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8]"><tr>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Period</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Country</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Employees</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Gross</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Ded</th>
              <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Net</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Handed off</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
              <th></th>
            </tr></thead>
            <tbody>{runs.map((r) => (
              <tr key={r.id} className="border-t border-black/5" data-testid={`acct-row-${r.id}`}>
                <td className="p-2 font-mono text-xs">{r.period_year}-{String(r.period_month).padStart(2, '0')}</td>
                <td className="p-2 text-xs">{r.country || 'ALL'}</td>
                <td className="p-2 text-right text-xs">{r.stats?.employees ?? 0}</td>
                <td className="p-2 text-right text-xs">{(r.stats?.gross || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className="p-2 text-right text-xs">{(r.stats?.deductions || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className="p-2 text-right text-xs font-bold">{(r.stats?.net || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className="p-2 text-xs">{r.handed_off_at ? new Date(r.handed_off_at).toLocaleDateString() : '—'}</td>
                <td className="p-2">
                  <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.accounts_status === 'posted' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{r.accounts_status || 'pending_post'}</span>
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => openDetail(r)} data-testid={`view-${r.id}`} className="text-xs text-[#0f5c3d] mr-2 inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> View</button>
                  {r.accounts_status !== 'posted' && (
                    <button onClick={() => markPosted(r)} data-testid={`post-${r.id}`} className="text-xs inline-flex items-center gap-0.5 px-2 py-1 rounded bg-emerald-600 text-white"><CheckCircle2 className="w-3 h-3" /> Post</button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
          {runs.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">Accounts inbox is empty — no runs handed off yet.</div>}
        </div>
      )}
      {showDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between">
              <div className="font-medium">{showDetail.period_year}-{String(showDetail.period_month).padStart(2, '0')} · {showDetail.country || 'ALL'} · {payslips.length} payslips</div>
              <button onClick={() => setShowDetail(null)} className="text-white/70 hover:text-white">✕</button>
            </div>
            <div className="overflow-y-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-[#f7fbf8] sticky top-0"><tr>
                  <th className="text-left p-2 text-xs">Employee</th>
                  <th className="text-right p-2 text-xs">Gross</th>
                  <th className="text-right p-2 text-xs">Deductions</th>
                  <th className="text-right p-2 text-xs">Net</th>
                </tr></thead>
                <tbody>{payslips.map((p) => (
                  <tr key={p.id} className="border-t border-black/5">
                    <td className="p-2 text-xs">{p.user_name}</td>
                    <td className="p-2 text-right">{p.gross.toFixed(2)}</td>
                    <td className="p-2 text-right">{p.deductions_total.toFixed(2)}</td>
                    <td className="p-2 text-right font-bold">{p.net.toFixed(2)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {showDetail.accounts_status !== 'posted' && (
              <div className="p-4 border-t border-black/5 bg-[#f7fbf8]">
                <button onClick={() => markPosted(showDetail)} data-testid="post-detail-btn" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-emerald-600 text-white font-semibold"><CheckCircle2 className="w-4 h-4" /> Post to Books ledger</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Tile = ({ label, v, accent }) => {
  const c = { emerald: 'text-emerald-700 bg-emerald-50', amber: 'text-amber-700 bg-amber-50' }[accent] || 'text-[#0b1a2e]';
  return (
    <div className={`rounded-2xl p-4 border border-black/5 ${c.includes('bg') ? c : 'bg-white ' + c}`}>
      <div className="text-2xl font-bold flex items-center gap-2"><TrendingUp className="w-5 h-5 opacity-60" />{v}</div>
      <div className="text-[10px] uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
};

export default AccountsQueuePage;
